import { Injectable, inject } from '@angular/core';
import {
    BehaviorSubject,
    Observable,
    Subject,
    combineLatest,
    map,
    take,
    switchMap,
} from 'rxjs';
import { StepLink } from '../../models/step-link';
import { NavigationExtras, Router } from '@angular/router';
import { ApplicationSteps } from '../../constants/application-steps';
import { StepValidationResult } from '../../models/step-validation-result';
import { DirectEntryStepService } from '../direct-entry-step/direct-entry-step.service';
import { routeMatchesRouteTemplate } from '../../../../../core/functions/string-helpers';

@Injectable({
    providedIn: 'root',
})
export class StepNavigationService {
    private readonly router = inject(Router);
    private readonly directEntryStepService = inject(DirectEntryStepService);

    private stepsSubject = new BehaviorSubject<StepLink[]>([]);
    steps$ = this.stepsSubject.asObservable();

    private nextClickSubject = new Subject<void>();
    nextClick$ = this.nextClickSubject.asObservable();

    private previousClickSubject = new Subject<void>();
    previousClick$ = this.previousClickSubject.asObservable();

    private saveAndCloseSubject = new Subject<void>();
    saveAndClose$ = this.saveAndCloseSubject.asObservable();

    private handoverToEwaSubject = new Subject<void>();
    handoverToEwa$ = this.handoverToEwaSubject.asObservable();

    private currentStep = new BehaviorSubject<StepLink | undefined>(undefined);
    currentStep$ = this.currentStep.asObservable();

    private submitForCoordinationSubject = new Subject<void>();
    submitForCoordinationClick$ =
        this.submitForCoordinationSubject.asObservable();

    private submitToFccSubject = new Subject<void>();
    submitToFccClick$ = this.submitToFccSubject.asObservable();

    private submitToPscaSubject = new Subject<void>();
    submitToPscaClick$ = this.submitToPscaSubject.asObservable();

    private submitToFiveDayHoldSubject = new Subject<void>();
    submitToFiveDayHoldClick$ = this.submitToFiveDayHoldSubject.asObservable();

    private proceedingToStep = new Subject<StepLink>();
    proceedingToStep$ = this.proceedingToStep.asObservable();

    /**
     * Initializes the application steps for a new application.
     */
    initializeNewApplicationSteps(): void {
        this.setSteps(ApplicationSteps.getNewApplicationSteps());
    }

    /**
     * Initializes the application steps for an unsupported application.
     */
    initializeUnsupportedApplicationSteps(applicationId: string): void {
        this.setSteps(
            ApplicationSteps.getUnsupportedApplicationSteps(applicationId),
        );
    }

    /**
     * Initializes the application preparation steps for a new Go application.
     */
    initializeGoNewApplicationPreparationSteps(): void {
        this.setSteps(ApplicationSteps.getGoNewApplicationPreparationSteps());
    }

    /**
     * Initializes the application preparation steps for an existing Go application.
     */
    initializeGoApplicationPreparationSteps(applicationId: string): void {
        this.setSteps(
            ApplicationSteps.getGoApplicationPreparationSteps(applicationId),
        );
    }

    /**
     * Initializes the application steps for a Go application.
     */
    initializeGoApplicationSteps(applicationId: string): void {
        this.setSteps(ApplicationSteps.getGoApplicationSteps(applicationId));
    }

    /**
     * Initializes the application steps for a coordination.
     */
    initializeCoordinationSteps(applicationId: string): void {
        this.setSteps(
            ApplicationSteps.getCoordinationApplicationSteps(applicationId),
        );
    }

    /**
     * Initializes the application steps for a new FRN filing.
     */
    initializeNewFrnFilingSteps(): void {
        this.setSteps(ApplicationSteps.getNewFrnFilingSteps());
    }

    /**
     * Initializes the application steps for an FRN filing.
     */
    initializeFrnFilingSteps(applicationId: string): void {
        this.setSteps(ApplicationSteps.getFrnFilingSteps(applicationId));
    }

    /**
     * Determines whether the provided URL path corresponds to a valid step in the current set of steps.
     *
     * @param urlPath The URL path to check against the current steps.
     * @returns True when there is a valid step matching the provided URL path; otherwise, false.
     */
    isUrlPathInSetSteps(urlPath: string): boolean {
        const steps = this.stepsSubject.getValue();
        const step = steps.find((s) =>
            routeMatchesRouteTemplate(urlPath, s.url),
        );
        return !!step;
    }

    /**
     * Sets the current step based on the provided URL parameter.
     *
     * @param urlPath The URL parameter used to identify the step.
     */
    setCurrentStepByPath(urlPath: string): void {
        this.steps$.pipe(take(1)).subscribe((steps) => {
            const step = steps.find((s) =>
                routeMatchesRouteTemplate(urlPath, s.url),
            );
            if (step) {
                this.currentStep.next(step);
            }
        });
    }

    /**
     * Gets the current step.
     *
     * @returns Current step.
     */
    getCurrentStep(): StepLink | undefined {
        return this.currentStep.getValue();
    }

    /**
     * Sets the validation status for each step in the currently set steps.
     *
     * @param validations Validation results for all steps.
     * @param navigateToFirstStep Whether to navigate to the first step after setting the validations. Default is true.
     */
    setStepValidations(
        validations: StepValidationResult[],
        navigateToFirstStep: boolean = true,
    ) {
        this.steps$.pipe(take(1)).subscribe((steps) => {
            const updatedSteps = steps.map((step) => {
                const validation = validations.find((v) => v.step === step.id);
                if (validation) {
                    step.passedValidation = validation.passedFinalValidation;
                }

                return step;
            });

            this.setSteps(updatedSteps, navigateToFirstStep);
        });
    }

    /**
     * Sets the correct application steps for the Direct Entry application type.
     *
     * @param applicationId Application's ID
     * @returns whether the correct steps are already set
     */
    setDirectEntrySteps(applicationId: string): Observable<boolean> {
        return this.steps$.pipe(
            take(1),
            switchMap((steps) => {
                return this.directEntryStepService
                    .getDirectEntryStepLinksForApplication(applicationId)
                    .pipe(
                        map((stepLinks: StepLink[]) => {
                            const alreadySetDirectEntryStepLinkUrls = steps.map(
                                (step) => step.url,
                            );

                            /**
                             * Checks to see if the direct entry step links for the application are already set.
                             * If they are not set, it will set them.
                             */
                            const directEntryStepLinksForApplicationAlreadySet =
                                stepLinks.every((expectedStep) =>
                                    alreadySetDirectEntryStepLinkUrls.includes(
                                        expectedStep.url,
                                    ),
                                ) && steps.length === stepLinks.length;

                            if (!directEntryStepLinksForApplicationAlreadySet) {
                                this.setSteps(stepLinks);
                            }

                            return directEntryStepLinksForApplicationAlreadySet;
                        }),
                    );
            }),
        );
    }

    /**
     * Sets the current steps validation status. This is meant to be used after a page is updated and before
     * you navigate to the next page.
     *
     * @param passedValidation whether the step passed validation.
     */
    setCurrentStepValidation(passedValidation: boolean): void {
        this.currentStep$.pipe(take(1)).subscribe((step) => {
            if (step) {
                step.passedValidation = passedValidation;
            }
        });
    }

    /**
     * Navigates to the first step in the currently set steps.
     */
    navigateToFirstStep(): void {
        this.steps$.pipe(take(1)).subscribe((steps) => {
            if (steps.length > 0) {
                const firstStep = steps[0];
                this.currentStep.next(firstStep);
                this.router.navigate([firstStep.url]);
            }
        });
    }

    /**
     * Emits an event to signal that the "next" button was clicked.
     */
    triggerNextClick(): void {
        this.nextClickSubject.next();
    }

    /**
     * Emits an event to signal that the "previous" button was clicked.
     */
    triggerPreviousClick(): void {
        this.previousClickSubject.next();
    }

    /**
     * Emits an event to signal that the "Save & Close" button was clicked.
     */
    triggerSaveAndCloseClick(): void {
        this.saveAndCloseSubject.next();
    }

    /**
     * Emits an event to signal that the "Handover to EWA" button was clicked.
     */
    triggerHandoverToEwaClick(): void {
        this.handoverToEwaSubject.next();
    }

    /**
     * Emits an event to signal that the "Submit for Coordination" button was clicked.
     */
    triggerSubmitForCoordinationClick(): void {
        this.submitForCoordinationSubject.next();
    }

    /**
     * Emits an event to signal that the "Submit to FCC" button was clicked.
     */
    triggerSubmitToFccClick(): void {
        this.submitToFccSubject.next();
    }

    /**
     * Emits an event to signal that the "Submit to PSCA" button was clicked.
     */
    triggerSubmitToPscaClick(): void {
        this.submitToPscaSubject.next();
    }

    /**
     * Emits an event to signal that the "Submit to 5-Day Hold" button was clicked.
     */
    triggerSubmitToFiveDayHoldClick(): void {
        this.submitToFiveDayHoldSubject.next();
    }

    /**
     * Emits an event to signal that the user is proceeding to a specific step.
     * @param stepLink the step the user is proceeding to.
     */
    triggerProceedToStep(stepLink: StepLink): void {
        this.proceedingToStep.next(stepLink);
    }

    /**
     * Advances to the next step in the application process and navigates to its URL.
     *
     * @param navExtras Optional navigation extras that should be sent to the following step.
     */
    goToNextStep(navExtras?: NavigationExtras): void {
        combineLatest([this.steps$, this.currentStep$])
            .pipe(take(1))
            .subscribe(([steps, currentStep]) => {
                if (currentStep) {
                    const currentIndex = steps.findIndex(
                        (step) => step.id === currentStep.id,
                    );
                    if (
                        currentIndex !== -1 &&
                        currentIndex < steps.length - 1
                    ) {
                        const nextStep = steps[currentIndex + 1];

                        this.router.navigate(
                            [nextStep.url],
                            navExtras || undefined,
                        );
                    }
                }
            });
    }

    /**
     * Moves to the previous step in the application process and navigates to its URL.
     *
     * @param navExtras Optional navigation extras that should be sent to the previous step.
     */
    goToPreviousStep(navExtras?: NavigationExtras): void {
        combineLatest([this.steps$, this.currentStep$])
            .pipe(take(1))
            .subscribe(([steps, currentStep]) => {
                if (currentStep) {
                    const currentIndex = steps.findIndex(
                        (step) => step.id === currentStep.id,
                    );
                    if (currentIndex > 0) {
                        const previousStep = steps[currentIndex - 1];

                        this.router.navigate(
                            [previousStep.url],
                            navExtras || undefined,
                        );
                    }
                }
            });
    }

    /**
     * Updates the application steps and sets the current step to the first step in the list if navigateToFirstStep is true.
     * If navigateToFirstStep is false, the current step will not be changed.
     *
     * @param steps List of application steps based on the application type.
     * @param navigateToFirstStep Whether to navigate to the first step after setting the steps. Default is true.
     */
    private setSteps(
        steps: StepLink[],
        navigateToFirstStep: boolean = true,
    ): void {
        this.stepsSubject.next(steps);
        if (navigateToFirstStep) {
            this.currentStep.next(steps[0]);
        }
    }
}
